/**
 * 资产库 Store (Pinia)
 * 
 * 管理资产库数据、选择状态、过滤排序等
 * 
 * @version 1.0.0
 * @author Frontend Team
 * @since 2025-10-23
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { ElMessage } from 'element-plus'
import { libraryAPI } from '@/api/library'
import type {
  Asset,
  AssetFilter,
  AssetSort,
  AssetStats,
  AssetTag,
  AssetGroup,
  CreateGroupRequest,
  UpdateGroupRequest
} from '@/types/library.types'

export const useLibraryStore = defineStore('library', () => {
  // ============= 状态定义 =============

  // 资产列表
  const assets = ref<Asset[]>([])
  const currentAsset = ref<Asset | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  // 过滤和排序
  const filter = ref<AssetFilter>({})
  const sort = ref<AssetSort>({ field: 'createdAt', order: 'desc' })

  // 分页
  const currentPage = ref(1)
  const pageSize = ref(20)
  const total = ref(0)

  // 选择状态
  const selectedAssetIds = ref<string[]>([])

  // 统计信息
  const stats = ref<AssetStats>({
    total: 0,
    audioCount: 0,
    zipCount: 0,
    totalSize: 0,
    totalDuration: 0,
    favoriteCount: 0
  })

  // 标签列表
  const tags = ref<AssetTag[]>([])

  // 分组
  const groups = ref<AssetGroup[]>([])
  const currentGroup = ref<AssetGroup | null>(null)
  const selectedGroupId = ref<string>('')

  // ============= 计算属性 =============

  /**
   * 过滤后的资产列表
   */
  const filteredAssets = computed(() => {
    let result = assets.value

    // 应用本地过滤（服务端已过滤，这里主要用于UI展示）
    if (filter.value.favorite !== undefined) {
      result = result.filter(asset => asset.favorite === filter.value.favorite)
    }

    if (filter.value.keyword) {
      const keyword = filter.value.keyword.toLowerCase()
      result = result.filter(asset =>
        asset.filename.toLowerCase().includes(keyword) ||
        asset.tags?.some(tag => tag.toLowerCase().includes(keyword))
      )
    }

    return result
  })

  /**
   * 选中的资产列表
   */
  const selectedAssets = computed(() => {
    return assets.value.filter(asset => selectedAssetIds.value.includes(asset.id))
  })

  /**
   * 是否全选
   */
  const isAllSelected = computed(() => {
    return assets.value.length > 0 && selectedAssetIds.value.length === assets.value.length
  })

  /**
   * 是否有选中项
   */
  const hasSelection = computed(() => {
    return selectedAssetIds.value.length > 0
  })

  /**
   * 过滤后的分组列表
   */
  const filteredGroups = computed(() => {
    return groups.value
  })

  /**
   * 当前选中的分组
   */
  const selectedGroup = computed(() => {
    if (!selectedGroupId.value) return null
    return groups.value.find(group => group.id === selectedGroupId.value) || null
  })

  // ============= 方法 =============

  /**
   * 加载资产列表
   */
  const loadAssets = async () => {
    isLoading.value = true
    error.value = null

    try {
      const response = await libraryAPI.getAssets(
        currentPage.value,
        pageSize.value,
        filter.value,
        sort.value
      )

      assets.value = response.list
      total.value = response.total

      // 同时更新统计信息中的总数，保持一致性
      stats.value.total = response.total
    } catch (err: any) {
      error.value = err.message || '加载资产列表失败'
      ElMessage.error(error.value || '加载资产列表失败')
      console.error('[LibraryStore] 加载资产列表失败:', err)
    } finally {
      isLoading.value = false
    }
  }

  /**
   * 加载资产详情
   */
  const loadAsset = async (assetId: string) => {
    isLoading.value = true
    error.value = null

    try {
      currentAsset.value = await libraryAPI.getAsset(assetId)
    } catch (err: any) {
      error.value = err.message || '加载资产详情失败'
      ElMessage.error(error.value || '加载资产详情失败')
      console.error('[LibraryStore] 加载资产详情失败:', err)
    } finally {
      isLoading.value = false
    }
  }

  /**
   * 更新资产
   */
  const updateAsset = async (
    assetId: string,
    updates: Partial<Pick<Asset, 'filename' | 'tags' | 'favorite' | 'meta'>>
  ) => {
    try {
      const updatedAsset = await libraryAPI.updateAsset(assetId, updates)
      
      // 更新列表中的资产
      const index = assets.value.findIndex(a => a.id === assetId)
      if (index !== -1) {
        assets.value[index] = updatedAsset
      }

      // 更新当前资产
      if (currentAsset.value?.id === assetId) {
        currentAsset.value = updatedAsset
      }

      ElMessage.success('更新成功')
      return updatedAsset
    } catch (err: any) {
      const errorMsg = err.message || '更新资产失败'
      ElMessage.error(errorMsg)
      console.error('[LibraryStore] 更新资产失败:', err)
      throw err
    }
  }

  /**
   * 删除资产
   */
  const deleteAsset = async (assetId: string) => {
    try {
      await libraryAPI.deleteAsset(assetId)
      
      // 从列表中移除
      assets.value = assets.value.filter(a => a.id !== assetId)
      total.value--

      // 清除当前资产
      if (currentAsset.value?.id === assetId) {
        currentAsset.value = null
      }

      ElMessage.success('删除成功')
    } catch (err: any) {
      const errorMsg = err.message || '删除资产失败'
      ElMessage.error(errorMsg)
      console.error('[LibraryStore] 删除资产失败:', err)
      throw err
    }
  }

  /**
   * 批量删除资产
   */
  const deleteAssets = async (assetIds: string[]) => {
    try {
      const response = await libraryAPI.deleteAssets(assetIds)
      
      // 从列表中移除
      assets.value = assets.value.filter(a => !assetIds.includes(a.id))
      total.value -= response.successCount

      // 清除选择
      selectedAssetIds.value = []

      ElMessage.success(`成功删除 ${response.successCount} 个资产`)
      return response
    } catch (err: any) {
      const errorMsg = err.message || '批量删除失败'
      ElMessage.error(errorMsg)
      console.error('[LibraryStore] 批量删除失败:', err)
      throw err
    }
  }

  /**
   * 切换收藏状态
   */
  const toggleFavorite = async (assetId: string, favorite: boolean) => {
    try {
      const updatedAsset = await libraryAPI.toggleFavorite(assetId, favorite)
      
      // 更新列表中的资产
      const index = assets.value.findIndex(a => a.id === assetId)
      if (index !== -1) {
        assets.value[index] = updatedAsset
      }

      // 更新当前资产
      if (currentAsset.value?.id === assetId) {
        currentAsset.value = updatedAsset
      }

      return updatedAsset
    } catch (err: any) {
      const errorMsg = err.message || '操作失败'
      ElMessage.error(errorMsg)
      console.error('[LibraryStore] 切换收藏失败:', err)
      throw err
    }
  }

  /**
   * 加载统计信息
   */
  const loadStats = async () => {
    try {
      stats.value = await libraryAPI.getStats()
    } catch (err: any) {
      console.error('[LibraryStore] 加载统计信息失败:', err)
    }
  }

  /**
   * 加载标签列表
   */
  const loadTags = async () => {
    try {
      tags.value = await libraryAPI.getTags()
    } catch (err: any) {
      console.error('[LibraryStore] 加载标签列表失败:', err)
    }
  }

  /**
   * 设置过滤条件
   */
  const setFilter = (newFilter: AssetFilter) => {
    filter.value = newFilter
    currentPage.value = 1
    loadAssets()
  }

  /**
   * 设置排序
   */
  const setSort = (newSort: AssetSort) => {
    sort.value = newSort
    loadAssets()
  }

  /**
   * 选择/取消选择资产
   */
  const toggleSelection = (assetId: string) => {
    const index = selectedAssetIds.value.indexOf(assetId)
    if (index > -1) {
      selectedAssetIds.value.splice(index, 1)
    } else {
      selectedAssetIds.value.push(assetId)
    }
  }

  /**
   * 全选/取消全选
   */
  const toggleSelectAll = () => {
    if (isAllSelected.value) {
      selectedAssetIds.value = []
    } else {
      selectedAssetIds.value = assets.value.map(a => a.id)
    }
  }

  /**
   * 清除选择
   */
  const clearSelection = () => {
    selectedAssetIds.value = []
  }

  /**
   * 清除错误
   */
  const clearError = () => {
    error.value = null
  }

  /**
   * 重置状态
   */
  const reset = () => {
    assets.value = []
    currentAsset.value = null
    filter.value = {}
    sort.value = { field: 'createdAt', order: 'desc' }
    currentPage.value = 1
    selectedAssetIds.value = []
    error.value = null
    groups.value = []
    currentGroup.value = null
    selectedGroupId.value = ''
  }

  // ============= 分组管理方法 =============

  /**
   * 加载分组列表
   */
  const loadGroups = async () => {
    try {
      const response = await libraryAPI.getGroups()
      groups.value = response.list
    } catch (err: any) {
      error.value = err.message || '加载分组列表失败'
      ElMessage.error(error.value || '加载分组列表失败')
      console.error('[LibraryStore] 加载分组列表失败:', err)
    }
  }

  /**
   * 创建分组
   */
  const createGroup = async (request: CreateGroupRequest) => {
    try {
      const group = await libraryAPI.createGroup(request)
      groups.value.unshift(group)
      ElMessage.success('分组创建成功')
      return group
    } catch (err: any) {
      error.value = err.message || '创建分组失败'
      ElMessage.error(error.value || '创建分组失败')
      console.error('[LibraryStore] 创建分组失败:', err)
      throw err
    }
  }

  /**
   * 更新分组
   */
  const updateGroup = async (groupId: string, request: UpdateGroupRequest) => {
    try {
      await libraryAPI.updateGroup(groupId, request)

      // 更新本地状态
      const index = groups.value.findIndex(g => g.id === groupId)
      if (index !== -1) {
        groups.value[index] = { ...groups.value[index], ...request }
      }

      ElMessage.success('分组更新成功')
    } catch (err: any) {
      error.value = err.message || '更新分组失败'
      ElMessage.error(error.value || '更新分组失败')
      console.error('[LibraryStore] 更新分组失败:', err)
      throw err
    }
  }

  /**
   * 删除分组
   */
  const deleteGroup = async (groupId: string) => {
    try {
      await libraryAPI.deleteGroup(groupId)

      // 更新本地状态
      groups.value = groups.value.filter(g => g.id !== groupId)

      // 如果删除的是当前选中的分组，清空选中状态
      if (selectedGroupId.value === groupId) {
        selectedGroupId.value = ''
      }

      ElMessage.success('分组删除成功')
    } catch (err: any) {
      error.value = err.message || '删除分组失败'
      ElMessage.error(error.value || '删除分组失败')
      console.error('[LibraryStore] 删除分组失败:', err)
      throw err
    }
  }

  /**
   * 批量移动资产到分组
   */
  const batchMoveToGroup = async (assetIds: string[], groupId: string) => {
    try {
      await libraryAPI.batchMoveToGroup({ assetIds, groupId })

      // 更新本地资产状态
      assets.value.forEach(asset => {
        if (assetIds.includes(asset.id)) {
          asset.groupId = groupId
        }
      })

      // 重新加载分组列表以更新资产数量
      await loadGroups()

      const message = groupId ? '资产已移动到分组' : '资产已移出分组'
      ElMessage.success(message)
    } catch (err: any) {
      error.value = err.message || '移动资产失败'
      ElMessage.error(error.value || '移动资产失败')
      console.error('[LibraryStore] 移动资产失败:', err)
      throw err
    }
  }

  /**
   * 设置选中的分组
   */
  const setSelectedGroup = (groupId: string) => {
    selectedGroupId.value = groupId

    // 更新过滤条件
    if (groupId) {
      setFilter({ ...filter.value, groupId })
    } else {
      const { groupId: _, ...newFilter } = filter.value
      setFilter(newFilter)
    }
  }

  return {
    // 状态
    assets,
    currentAsset,
    isLoading,
    error,
    filter,
    sort,
    currentPage,
    pageSize,
    total,
    selectedAssetIds,
    stats,
    tags,
    groups,
    currentGroup,
    selectedGroupId,

    // 计算属性
    filteredAssets,
    selectedAssets,
    isAllSelected,
    hasSelection,
    filteredGroups,
    selectedGroup,

    // 方法
    loadAssets,
    loadAsset,
    updateAsset,
    deleteAsset,
    deleteAssets,
    toggleFavorite,
    loadStats,
    loadTags,
    setFilter,
    setSort,
    toggleSelection,
    toggleSelectAll,
    clearSelection,
    clearError,
    reset,

    // 分组方法
    loadGroups,
    createGroup,
    updateGroup,
    deleteGroup,
    batchMoveToGroup,
    setSelectedGroup
  }
})

